import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db, storage } from "../firebase";
import {
  doc, getDoc,
  collection, addDoc,
  query, where, getDocs,
  deleteDoc,
  updateDoc
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { signOut } from "firebase/auth";

function Home() {
  const navigate = useNavigate();

  const [userData, setUserData] = useState(null);
  const [activeTab, setActiveTab] = useState("daily");
  const [posts, setPosts] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [textInput, setTextInput] = useState("");
  const [editingPost, setEditingPost] = useState(null);

  const [menuOpenId, setMenuOpenId] = useState(null);

  const [currentDate, setCurrentDate] = useState(new Date());
  const [time, setTime] = useState(new Date());

  const user = auth.currentUser;

  const [scrolled, setScrolled] = useState(false);
  const rightRef = useRef(null);
  const location = useLocation();
  const [showProfile, setShowProfile] = useState(false);
  

  useEffect(() => {
    const el = rightRef.current;
    if (!el) return;

    const handleScroll = () => {
      setScrolled(el.scrollTop > 10); // ✅ THIS is the fix
    };

    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, []);

  // ⏰ clock
  useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // 👤 fetch user
  useEffect(() => {
    const fetchUser = async () => {
      if (!user) return;

      const snap = await getDoc(doc(db, "users", user.uid));
      if (snap.exists()) setUserData(snap.data());
      else navigate("/create");
    };

    fetchUser();
  }, [navigate, user]);

  // 📅 format
  const formatDate = (date) => date.toISOString().split("T")[0];

  const todayString = formatDate(new Date());
  const selectedString = formatDate(currentDate);

  const isToday = todayString === selectedString;

  // 🔥 FETCH POSTS (FIXED ORDER)
  const fetchPosts = async () => {
    if (!user) return;

    const q = query(
      collection(db, "posts"),
      where("userId", "==", user.uid)
    );

    const snap = await getDocs(q);

    const filtered = snap.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(p => p.date === selectedString)
      .sort((a, b) => Number(b.createdAt) - Number(a.createdAt)); // 🔥 FIX

    setPosts(filtered);
    const unknownCheckins = filtered.filter(
  p => p.type === "checkin" && p.isFake
);

for (const checkin of unknownCheckins) {
  const hasPostsBelow = filtered.some(
    p =>
      p.type !== "checkin" &&
      Number(p.createdAt) > Number(checkin.createdAt)
  );

  // ❌ no posts under it → delete it
  if (!hasPostsBelow) {
    await deleteDoc(doc(db, "posts", checkin.id));
  }
}
  };

  useEffect(() => {
    fetchPosts();
  }, [currentDate, user]);

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/");
  };

  // ✍️ ADD / EDIT TEXT
  const handleSaveText = async () => {
    if (!textInput.trim() || !user || !isToday) return;

    if (editingPost) {
      await updateDoc(doc(db, "posts", editingPost.id), {
        content: textInput
      });
    } else {
      await addDoc(collection(db, "posts"), {
        userId: user.uid,
        type: "text",
        content: textInput,
        date: todayString,
        createdAt: Date.now() // 🔥 consistent timestamp
      });
    }

    setTextInput("");
    setEditingPost(null);
    setShowModal(false);
    fetchPosts();
  };

  // 🖼 ADD PHOTO
    const handleAddPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file || !user || !isToday) return;

    try {
      const fileRef = ref(storage, `posts/${user.uid}/${Date.now()}_${file.name}`);
      await uploadBytes(fileRef, file);

      const downloadURL = await getDownloadURL(fileRef);

    await addDoc(collection(db, "posts"), {
      userId: user.uid,
      type: "photo",
      content: downloadURL,
      date: todayString,
      createdAt: Date.now()
    });

    e.target.value = null; // ✅ reset input
    fetchPosts();
    } catch (err) {
    console.error("Upload error:", err);
    }
  };

  // ❌ DELETE
const handleDelete = async (post) => {

  if (post.type === "checkin") {
    // ✅ convert to UNKNOWN PLACE instead of deleting
    await updateDoc(doc(db, "posts", post.id), {
      content: "Unknown place",
      isFake: true
    });

  } else {
    // ✅ normal delete for posts
    await deleteDoc(doc(db, "posts", post.id));
  }

  setMenuOpenId(null);
  fetchPosts();
};

  // ⬅️➡️ NAVIGATION
  const changeDate = (dir) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);

    if (activeTab === "daily") {
      newDate.setDate(newDate.getDate() + dir);
    }

    if (activeTab === "monthly") {
      newDate.setMonth(newDate.getMonth() + dir);
      newDate.setDate(1); // ✅ prevent overflow bug
    }

    if (activeTab === "yearly") {
      newDate.setFullYear(newDate.getFullYear() + dir);
      newDate.setMonth(0); // ✅ reset safely
      newDate.setDate(1);
    }

    return newDate;
    });
  };

  const getDaysInMonth = () => {
  const y = currentDate.getFullYear();
  const m = currentDate.getMonth();
  return new Date(y, m + 1, 0).getDate();
};

  // 📅 month days
  const formatMonth = (d) =>
    d.toLocaleString('default', { month: 'long', year: 'numeric' });

  const formatYear = (d) => d.getFullYear();

  const formatDay = (d) =>
  new Date(d).toLocaleString('default', { weekday: 'long' });

  // 🕒 12-HOUR FORMAT
  const formatTime = (d) =>
  new Date(d).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });

  const formatPrettyDate = (d) => {
    const dateObj = new Date(d); // ✅ force conversion

    return dateObj.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const themeColor = userData?.palette || "#E9D6D2";
  const bgImage = userData?.background;
  const overlay = userData?.overlay || "light";

  const overlayStyle =
    overlay === "dark"
      ? "rgba(0,0,0,0.6)"
      : "rgba(255,255,255,0.6)";

  const cardStyle =
    overlay === "dark"
      ? "rgba(0,0,0,0.4)"
      : "rgba(255,255,255,0.7)";

  const textColor = overlay === "dark" ? "#fff" : "#000";

  const handleCheckIn = () => {
  if (!navigator.geolocation || !user || !isToday) return;

  navigator.geolocation.getCurrentPosition(
    async (position) => {
      const { latitude, longitude } = position.coords;

      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
        );
        const data = await res.json();

        const city =
          data.address.city ||
          data.address.town ||
          data.address.municipality ||
          data.address.village ||
          data.address.county ||
          "Unknown place";

        const province =
          data.address.state ||
          data.address.region ||
          data.address.province ||
          "";

        const locationText = province
          ? `${city}, ${province}`
          : city;

        await addDoc(collection(db, "posts"), {
          userId: user.uid,
          type: "checkin",
          content: locationText,
          date: todayString,
          createdAt: Date.now()
        });

        fetchPosts();
      } catch (err) {
        console.error("Check-in error:", err);
      }
    },
    (err) => {
      console.error("Location error:", err);
    }
  );
};

const groupedPosts = [];
let currentGroup = null;

[...posts].reverse().forEach((post) => {

  if (post.type === "checkin") {
    // ✅ start new group with real check-in
    currentGroup = { checkin: post, posts: [] };
    groupedPosts.push(currentGroup);

  } else {
    // ✅ if no check-in yet → create UNKNOWN PLACE
    if (!currentGroup) {
      currentGroup = {
  checkin: {
    content: "Unknown place",
    isFake: true,
    createdAt: post.createdAt // ✅ ADD THIS
  },
  posts: []
};
      groupedPosts.push(currentGroup);
    }

    currentGroup.posts.unshift(post); // keep newest on top
  }

});

// ✅ newest check-in group on top
groupedPosts.reverse();

  return (
    <div style={{
      ...styles.page,
      background: bgImage
        ? `url(${bgImage}) center/cover no-repeat`
        : themeColor
    }}>

      {bgImage && (
        <div style={{
          position: "fixed",
          inset: 0,
          background: overlayStyle,
          zIndex: 0
        }} />
      )}

<div style={styles.floatingProfile(showProfile)}>

            {userData?.photo ? (
              <img src={userData.photo} style={styles.avatar} alt="avatar" />
            ) : (
              <div style={styles.avatar}>
                {userData?.name?.charAt(0)?.toUpperCase()}
              </div>
            )}

            <h3>{userData?.name}</h3>
            <p style={styles.meta}>{userData?.country}</p>

            <label
  onClick={handleCheckIn}
  style={styles.actionBtn}
  disabled={!isToday}
>
  Check-in
</label>

            <label onClick={() => setShowModal(true)} style={styles.actionBtn} disabled={!isToday}>
              Add Text
            </label>

            <label style={{
              ...styles.actionBtn,
              opacity: isToday ? 1 : 0.5,
              pointerEvents: isToday ? "auto" : "none"
            }}>
              Add Photo
              <input type="file" accept="image/*" onChange={handleAddPhoto} hidden />
            </label>

            <button onClick={() => navigate("/update")} style={styles.updateBtn}>
              Update
            </button>

            <button onClick={handleLogout} style={styles.logoutBtn}>
              Logout
            </button>

          </div>

<button
  onClick={() => setShowProfile(prev => !prev)}
  style={styles.toggleBtn(showProfile)}
>
  {showProfile ? "<" : ">"}
</button>


    <div style={{ ...styles.card, background: cardStyle, color: textColor }}>

    <div style={styles.leftNav}>
    <span
      onClick={() => navigate("/home")}
      style={{
        ...styles.navLink,
        fontWeight: location.pathname === "/home" ? "bold" : "normal"
      }}
    >
      Home
    </span>

    <span
      onClick={() => navigate("/engage")}
      style={{
        ...styles.navLink,
        fontWeight: location.pathname === "/engage" ? "bold" : "normal"
      }}
    >
      Engage
    </span>

    <span
      onClick={() => navigate("/yard")}
      style={{
        ...styles.navLink,
        fontWeight: location.pathname === "/yard" ? "bold" : "normal"
      }}
    >
      Yard
    </span>
  </div>

          {/* HEADER */}
          <div style={styles.stickyHeader(scrolled)}>

          {/* TABS */}
          <div style={styles.tabs}>
            {["daily", "monthly", "yearly"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  ...styles.tabBtn,
                  background: activeTab === tab ? themeColor : "transparent",
                }}
              >
                {tab.toUpperCase()}
              </button>
            ))}
          </div>

          {/* HEADER CONTENT */}
            <div style={styles.headerCenter}>

              <span style={styles.arrow} onClick={() => changeDate(-1)}>{"<"}</span>

              <div style={{ textAlign: "center" }}>
                {activeTab === "daily" && (
                  <>
                    <h3>{formatDay(currentDate)}, {formatPrettyDate(currentDate)} - {formatTime(time)}</h3>
                  </>
                )}

                {activeTab === "monthly" && (
                  <h3>{formatMonth(currentDate)}</h3>
                )}

          {/* MONTHLY */}
          {activeTab === "monthly" && (
            <div style={styles.calendar}>
              {Array.from({ length: getDaysInMonth() }, (_, i) => i + 1).map(day => (
                <div
                  key={day}
                  style={styles.dayBox}
                  onClick={() => {
                    const newDate = new Date(currentDate);
                    newDate.setDate(day);
                    setCurrentDate(newDate);
                    setActiveTab("daily");
                  }}
                >
                  {day}
                </div>
              ))}
            </div>
          )}
                {activeTab === "yearly" && (
                  <h3>{formatYear(currentDate)}</h3>
                )}
              </div>

              <span style={styles.arrow} onClick={() => changeDate(1)}>{">"}</span>
            </div>
          </div>

          {activeTab === "yearly" && (
  <div style={styles.calendar}>
    {Array.from({ length: 12 }, (_, i) => {
      const month = new Date(currentDate.getFullYear(), i);

      return (
        <div
          key={i}
          style={styles.dayBox}
          onClick={() => {
            setCurrentDate(month);
            setActiveTab("monthly");
          }}
        >
          {month.toLocaleString('default', { month: 'short' })}
        </div>
      );
    })}
  </div>
)}
          {/* DAILY */}
          {activeTab === "daily" && (
            posts.length === 0 ? (
              <div style={{ ...styles.noEntry, color: textColor }}>
                No entry yet ✨
              </div>  
            ) : (
              <div style={styles.grid}>

      {groupedPosts.map((group, i) => (
        <React.Fragment key={i}>

          {/* CHECK-IN HEADER */}
          {group.checkin && (
            <div
              style={{
                gridColumn: "1 / -1",
                background: cardStyle,
                padding: "10px 15px",
                borderRadius: "10px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}
            >
              <span>
  📍 {group.checkin.content} • {formatTime(new Date(group.checkin.createdAt))}
</span>

{!group.checkin.isFake && (
              <div>
                <span
                  onClick={() =>
                    setMenuOpenId(
                      menuOpenId === group.checkin.id ? null : group.checkin.id
                    )
                  }
                >
                  ⋮
                </span>

                {menuOpenId === group.checkin.id && (
                  <div>
                    <div onClick={() => handleDelete(group.checkin)}>
                      Delete
                    </div>
                  </div>
                )}
              </div>
              )}
            </div>
          )}

          {/* POSTS */}
          {group.posts.map((post) => (
            <div
              key={post.id}
              style={{
                ...styles.gridItem,
                background: cardStyle,
                position: "relative",
                display: "flex",
                flexDirection: "column"
              }}
            >
              <div style={{ position: "absolute", top: 5, right: 5 }}>
                <span
                  onClick={() =>
                    setMenuOpenId(menuOpenId === post.id ? null : post.id)
                  }
                >
                  ⋮
                </span>

                {menuOpenId === post.id && (
                  <div>
                    <div onClick={() => handleDelete(post)}>Delete</div>
                  </div>
                )}
              </div>

              <div style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}>
                {post.type === "text" && <p>{post.content}</p>}
                {post.type === "photo" && (
                  <img src={post.content} alt="" style={styles.image} />
                )}
              </div>

              <div style={{
                fontSize: "10px",
                marginTop: "auto",
                alignSelf: "flex-end"
              }}>
                {formatTime(new Date(post.createdAt))}
              </div>
            </div>
          ))}

        </React.Fragment>
      ))}

    </div>
  )
)}

{/* ✅ CLOSE RIGHT + MAIN PROPERLY */}
</div>

      {/* MODAL */}
      {showModal && (
        <div style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.6)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10
        }}>
          <div style={{
            background: "#fff",
            padding: "20px",
            borderRadius: "10px",
            width: "300px"
          }}>
            <textarea
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              placeholder="Write something..."
              style={{ width: "100%", height: "100px" }}
            />

            <button onClick={handleSaveText}>Save</button>
            <button onClick={() => setShowModal(false)}>Cancel</button>
          </div>
        </div>
      )}

    </div>
  );
}

const styles = {
  page: {
    height: "100vh",
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    overflowY: "auto",
    overflowX: "hidden",
    position: "relative"
  },

  stickyHeader: (scrolled) => ({
    position: "sticky",
    top: 0,
    zIndex: 5,
    boxShadow: scrolled ? "0 2px 8px rgba(0,0,0,0.08)" : "none",
    marginBottom: "10px",
    background: scrolled
    ? "#ffffff"   // 👈 solid white when scrolling
    : "rgba(255,255,255,0.85)",   // 👈 lighter when at top
    transition: "0.25s ease"
  }),

  headerCenter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },

  arrow: {
    fontSize: "30px",
    cursor: "pointer",
    padding: "10px"
  },

  card: {
    width: "100%",
    maxWidth: "800px",
    minHeight: "100vh",
    padding: "25px",
    textAlign: "center",
    backdropFilter: "blur(1px)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },

  avatar:{width:"120px",height:"120px",borderRadius:"50%",objectFit:"cover",margin:"0 auto 15px"},

  textarea: {
    width: "100%",
    minHeight: "80px",
    borderRadius: "10px",
    border: "none",
    padding: "10px",
    marginTop: "15px",
    resize: "none"
  },

  actionBtn: {
    marginTop: "10px",
    padding: "8px",
    width: "93%",
    borderRadius: "20px",
    border: "none",
    background: "#696969",
    color: "#fff",
    cursor: "pointer",
    display: "block"
  },

  updateBtn: {
    marginTop: "15px",
    padding: "10px",
    width: "100%",
    borderRadius: "20px",
    border: "none",
    background: "#444",
    color: "#fff"
  },

  logoutBtn: {
    marginTop: "10px",
    padding: "10px",
    width: "100%",
    borderRadius: "20px",
    border: "none",
    background: "#E9D6D2"
  },

  tabs: {
    display: "flex",
    gap: "10px",
    marginBottom: "5px"
  },

  tabBtn: {
    flex: 1,
    padding: "10px",
    borderRadius: "20px",
    border: "none",
    cursor: "pointer"
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "10px"
  },

  gridItem: {
    aspectRatio: "1 / 1",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.8rem",
    padding: "5px"
  },

  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover"
  },

  noEntry: {
    textAlign: "center",
    marginTop: "50px",
    opacity: 0.7
  },

  calendar: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: "8px"
  },

  dayBox: {
    padding: "10px",
    borderRadius: "8px",
    background: "#ffe6e6",
    textAlign: "center",
    cursor: "pointer"
  },

  yearList: {
    display: "flex",
    flexDirection: "column",
    gap: "10px"
  },

  yearItem: {
    padding: "15px",
    borderRadius: "10px",
    background: "#ffeaea",
    textAlign: "center",
    cursor: "pointer"
  },

  meta: {
    fontSize: "0.8rem",
    opacity: 0.8
  },

  leftNav: {
  display: "flex",
  gap: "15px",
  marginBottom: "10px",
  paddingLeft: "5px"
},

navLink: {
  cursor: "pointer",
  fontSize: "14px",
  opacity: 0.7
},

tabsCentered: {
  display: "flex",
  gap: "10px",
  justifyContent: "center",
  marginBottom: "5px"
},

toggleBtn: (showProfile) => ({
  position: "fixed",
  left: showProfile ? "260px" : "10px",
  top: "20px",
  width: "35px",
  height: "35px",
  borderRadius: "50%",
  border: "none",
  cursor: "pointer",
  zIndex: 9999,
  background: "#fff",
  boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
  transition: "0.3s ease"
}),

floatingProfile: (showProfile) => ({
  position: "fixed",
  left: 0,
  top: 0,
  height: "100vh",
  width: "260px",
  padding: "20px",
  background: "rgba(255,255,255,0.85)",
  backdropFilter: "blur(12px)",
  transform: showProfile ? "translateX(0)" : "translateX(-100%)",
  transition: "0.3s ease",
  zIndex: 9998,   // 🔥 must be higher than everything except toggle
  boxShadow: "2px 0 10px rgba(0,0,0,0.1)"
}),
};

export default Home;